function hasCompleteLinkSyntax(text) {
    return (text.lastIndexOf('[') <= text.lastIndexOf(']') &&
        text.lastIndexOf('](') <= text.lastIndexOf(')'));
}
/** Separate a speakable sentence from buffered model tokens without reading raw code aloud. */
export function takeSpeech(buffer, final = false, inCode = false) {
    const sentences = [];
    let rest = buffer;
    while (rest.length) {
        let boundary = 0;
        for (const match of rest.matchAll(/[.!?](?:[ \t]|$)|\n/g)) {
            const candidate = match.index + match[0].length;
            const prefix = rest.slice(0, candidate);
            if (inCode || /^\s*(?:`{3,}|~{3,})/.test(prefix) || hasCompleteLinkSyntax(prefix)) {
                boundary = candidate;
                break;
            }
        }
        if (!boundary)
            break;
        const sentence = rest.slice(0, boundary);
        if (/^\s*(?:`{3,}|~{3,})/.test(sentence) && (sentence.match(/`{3,}|~{3,}/g)?.length ?? 0) % 2)
            inCode = !inCode;
        sentences.push(sentence);
        rest = rest.slice(boundary);
    }
    if (final && rest.trim()) {
        sentences.push(rest);
        rest = '';
    }
    const bounded = [];
    for (let sentence of sentences) {
        while (sentence.length > 900) {
            const space = sentence.lastIndexOf(' ', 900);
            const boundary = space > 0 ? space : 900;
            bounded.push(sentence.slice(0, boundary));
            sentence = sentence.slice(boundary);
        }
        bounded.push(sentence);
    }
    return {
        ready: bounded.map((sentence) => sentence.trim()).filter(Boolean),
        rest,
    };
}
/** Choose an unchanged, complete-word prefix without cutting a code fence or Markdown link. */
function speechPhraseBoundary(text, deadline = false, minimum = 30, maximum = 100) {
    if (/^\s*[`~]/.test(text))
        return 0;
    const safe = (boundary) => {
        const prefix = text.slice(0, boundary);
        return (prefix.trim().length >= minimum &&
            !/`{3,}|~{3,}/.test(prefix) &&
            (prefix.match(/`/g)?.length ?? 0) % 2 === 0 &&
            hasCompleteLinkSyntax(prefix));
    };
    const limit = Math.min(text.length, maximum);
    for (let index = 0; index < limit; index++) {
        if (/[,;:]/.test(text[index] ?? '') && /\s/.test(text[index + 1] ?? '') && safe(index + 1))
            return index + 1;
    }
    if (!deadline && text.length < maximum)
        return 0;
    // A trailing token may be only part of a word; split only at an observed whitespace boundary.
    for (let index = limit; index > 0; index--) {
        if (/\s/.test(text[index] ?? '') && safe(index))
            return index;
    }
    return 0;
}
/** Shared phrase policy for browser fallback and server-owned synthesis. */
export class SpeechSegmenter {
    onPhrase;
    prose = [];
    speechStarted = false;
    speechTimer;
    firstSpeechTimer;
    continuationTimer;
    buffer = '';
    inCode = false;
    constructor(onPhrase) {
        this.onPhrase = onPhrase;
    }
    append(text, final = false) {
        this.buffer += text;
        const parts = takeSpeech(this.buffer, final, this.inCode);
        this.buffer = parts.rest;
        for (const sentence of parts.ready) {
            // Artifacts have their own visual treatment. Never read fenced programs or diagrams aloud.
            const fences = (sentence.match(/`{3,}|~{3,}/g) ?? []).length;
            if (/^\s*(?:`{3,}|~{3,})/.test(sentence)) {
                this.flushProse(true);
                if (fences % 2)
                    this.inCode = !this.inCode;
                continue;
            }
            if (!this.inCode)
                this.prose.push(sentence);
        }
        this.flushProse(final);
        if (!this.speechStarted && !this.inCode)
            this.flushFirstPhrase(false);
        if (!this.speechStarted && !this.inCode && speechPhraseBoundary(this.buffer, true)) {
            // Anchor this deadline to the first usable phrase, not the most recent model token.
            this.firstSpeechTimer ??= setTimeout(() => {
                this.firstSpeechTimer = undefined;
                if (!this.speechStarted && !this.inCode)
                    this.flushFirstPhrase(true);
            }, 150);
        }
        else {
            clearTimeout(this.firstSpeechTimer);
            this.firstSpeechTimer = undefined;
        }
        if (this.speechStarted && !this.inCode)
            this.flushContinuation(false);
        if (this.speechStarted && !this.inCode && speechPhraseBoundary(this.buffer, true, 90, 280)) {
            // A later long sentence must not wait for its full stop after the first audio has played.
            this.continuationTimer ??= setTimeout(() => {
                this.continuationTimer = undefined;
                if (!this.inCode)
                    this.flushContinuation(true);
            }, 250);
        }
        else {
            clearTimeout(this.continuationTimer);
            this.continuationTimer = undefined;
        }
        if (this.prose.length && this.speechTimer === undefined)
            this.speechTimer = setTimeout(() => {
                this.speechTimer = undefined;
                if (!this.speechStarted && !this.inCode)
                    this.flushFirstPhrase(true);
                this.flushProse(true);
            }, 100);
        else if (!this.prose.length) {
            clearTimeout(this.speechTimer);
            this.speechTimer = undefined;
        }
    }
    flushFirstPhrase(deadline) {
        const readyLength = this.prose.join(' ').length;
        const boundary = speechPhraseBoundary(this.buffer, deadline, Math.max(1, 30 - readyLength));
        if (!boundary)
            return;
        this.prose.push(this.buffer.slice(0, boundary).trim());
        this.buffer = this.buffer.slice(boundary);
        this.flushProse(false);
    }
    flushContinuation(deadline) {
        let boundary = speechPhraseBoundary(this.buffer, deadline, 90, 280);
        while (boundary) {
            this.prose.push(this.buffer.slice(0, boundary).trim());
            this.buffer = this.buffer.slice(boundary);
            this.flushProse(true);
            boundary = speechPhraseBoundary(this.buffer, deadline, 90, 280);
        }
    }
    flushProse(final) {
        while (this.prose.length) {
            const available = this.prose.reduce((length, part) => length + part.length + 1, 0);
            const target = this.speechStarted ? 650 : 30;
            if (!final && available < target)
                break;
            const limit = this.speechStarted ? 900 : 100;
            let text = this.prose.shift() ?? '';
            if (!this.speechStarted && text.length > limit) {
                const boundary = speechPhraseBoundary(text, true);
                if (boundary) {
                    this.prose.unshift(text.slice(boundary).trim());
                    text = text.slice(0, boundary).trim();
                }
            }
            while (this.prose.length && text.length + (this.prose[0]?.length ?? 0) + 1 <= limit) {
                text += ` ${this.prose.shift() ?? ''}`;
            }
            if (text)
                this.onPhrase(text);
            this.speechStarted = true;
        }
    }
    reset() {
        clearTimeout(this.speechTimer);
        clearTimeout(this.firstSpeechTimer);
        clearTimeout(this.continuationTimer);
        this.speechTimer = undefined;
        this.firstSpeechTimer = undefined;
        this.continuationTimer = undefined;
        this.prose = [];
        this.speechStarted = false;
        this.buffer = '';
        this.inCode = false;
    }
}
